/**
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const NatObj = require('../types/NatObj')
const kernel = require('../kernel/kernel')
const StyleEncadrement = require('../types/StyleEncadrement')
const CAffLiePt = require('./CAffLiePt')

module.exports = CImage

/**
 * Classe représentant une image. Elle est chargée sous forme binaire.
 * @constructor
 * @extends CAffLiePt
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {CImplementationProto} impProto : null ou la construction propriétaire.
 * @param {boolean} estElementFinal : true si élément final de construction.
 * @param {Color} couleur : La couleur de l'objet.
 * @param x_nom : Abscisse d'affichage
 * @param y_nom : Ordonnée d'affichage
 * @param {int} dec_x : Décalage horizontal par rapport à x_nom
 * @param {int} dec_y : Décalage vertical par rapport à y_nom
 * @param {boolean} masque : true si l'objet est masqué.
 * @param {CPt} pointLie : null ou le point auquel l'image est liée.
 * @param {int} taillePolice : Indice donnant la taille de la police.
 * @param {StyleEncadrement} encadrement
 * @param {boolean} effacementFond : true si on efface le fond avant d'afficher l'image.
 * @param {Color} couleurFond : La couleur de fond éventuelle.
 * @param {int} alignementHorizontal : 0 pour alignement gauche, 1 pour centre, 2 pour droite.
 * @param {int} alignementVertical : 0 pour alignement vers le haut, 1 pour centré, 2 pour bas.
 * @param {string} natImage : chaîne contenant la nature de l'image : "png","jpg" ou "gif"
 * @param {string} image : chaîne de caractères en Base64 contenant le code binaire de l'image.
 * @param {CValeurAngle} angText : L'angle du texte par rapport à l'horizontale
 * @constructor
 */
function CImage (listeProprietaire, impProto, estElementFinal, couleur,x_nom, y_nom,
    dec_x, dec_y, masque, pointLie, taillePolice, encadrement, effacementFond, couleurFond, alignementHorizontal,
     alignementVertical, natImage, image, angText) {
  if (arguments.length === 1) CAffLiePt.call(this, listeProprietaire);
  else {
    CAffLiePt.call(this, listeProprietaire, impProto, estElementFinal, couleur,
      x_nom, y_nom, dec_x, dec_y, masque, pointLie, taillePolice, encadrement, effacementFond,
      couleurFond, alignementHorizontal, alignementVertical, angText);
    this.natImage = natImage;
    this.image = image;
  }
}
CImage.prototype = new CAffLiePt();
CImage.prototype.constructor = CImage;
CImage.prototype.superClass = "CAffLiePt";
CImage.prototype.className = "CImage";

CImage.prototype.numeroVersion = function() {
  return 2;
};

CImage.prototype.getClone = function(listeSource, listeCible) {
  var ind1 = listeSource.indexOf(this.pointLie);
  var ind2 = listeSource.indexOf(this.impProto);
  var angTextClone = this.angText.getClone(listeSource, listeCible);
  var ptelb = new CImage(listeCible, listeCible.get(ind2, "CImplementationProto"),
      this.estElementFinal, this.couleur, this.x_nom, this.y_nom, this.dec_x, this.dec_y,
      this.masque, listeCible.get(ind1, "CPt"), this.taillePolice, this.encadrement,
      this.effacementFond, this.couleurFond, this.alignementHorizontal, this.alignementVertical,
      this.natImage, this.image, angTextClone);
  ptelb.width = this.width;
  ptelb.height = this.height;
  return ptelb;
};
// Ajout version 6.0
CImage.prototype.setReady4MathJax = function() {
}

CImage.prototype.setReady4MathJaxUpdate = function() {
}

CImage.prototype.positionne = function(infoRandom, dimfen) {
  CAffLiePt.prototype.positionnePourIm.call(this, infoRandom, dimfen);
  if (!this.existe) return;
  /** Modifié version 6.0
  this.rectAff.x = this.x_nom + this.dec_x;
  this.rectAff.y = this.y_nom + this.dec_y;
   */
  this.rectAff.x = 0;
  this.rectAff.y = 0;

  // Modification version 4.5
  switch (this.alignementHorizontal) {
  case CAffLiePt.alignHorCent :
    this.rectAff.x -= this.width/2;
    break;
  case CAffLiePt.alignHorRight :
    this.rectAff.x -= this.width;
  }
  switch (this.alignementVertical) {
    case CAffLiePt.alignVerCent :
      this.rectAff.y -= this.height/2;
      break;
    case CAffLiePt.alignVerLow :
      this.rectAff.y -= this.height;
  }
};
CImage.prototype.createg = function() {
  var g, image;
  g = kernel.cens("g");
  this.rectAff.width = this.width + 3;
  this.rectAff.height = this.height + 3;
  image = kernel.cens("image",{
    x : this.rectAff.x,
    y : this.rectAff.y,
    width : this.width,
    height : this.height,
    "pointer-events" : "none"
  });
  var tab = ['png','gif','jpg'];
  var ext = tab[this.natImage];
  image.setAttributeNS( "http://www.w3.org/1999/xlink", "href", "data:image/"+ ext +";base64," + kernel.base64Encode(this.image,true));
  g.appendChild(image);
  if (this.effacementFond || (this.encadrement !== StyleEncadrement.Sans)) this.creeRectangle(g);
  // Ajout version Version 6.0 //////////////////////////////////////////
  this.positionneAngText(g);
  /////////////////////////////////////////////////////////////////////////

  return g;
};
CImage.prototype.update = function(svg) {
  if (this.g !== null) {
    var img = this.g.childNodes[this.g.childNodes.length - 1];
    // g.removeAttribute("x");
    // g.removeAttribute("y");
    img.setAttribute("x",this.rectAff.x);
    img.setAttribute("y",this.rectAff.y);
    if (this.effacementFond || (this.encadrement !== StyleEncadrement.Sans))  this.remplaceRectangle(this.g, this.couleurFond);
    this.positionneAngText(this.g);
  }
};
CImage.prototype.ajouteAntecedents = function(liste) {
  if (this.pointLie !== null) liste.add(this.pointLie);
};
CImage.prototype.getNature = function() {
  return NatObj.NImage;
};
CImage.prototype.rendChaineAffichage = function() {
  return "";
};
CImage.prototype.chaineDesignation = function() {
  return "desImage";
};

// Rajout version 6.0 pour pouvoir créer un lieu d'objet d'un image
CImage.prototype.setClone = function(ptel) {
  CAffLiePt.prototype.setClone.call(this, ptel);
  this.rectAff.setClone(ptel.rectAff);
};

CImage.prototype.read = function(inps, list){
  CAffLiePt.prototype.read.call(this, inps, list);
  this.natImage = inps.readByte();
  this.width = inps.readInt();
  this.height = inps.readInt();
  this.image = inps.readBufferedImage();
};
CImage.prototype.write = function(oups, list){
 CAffLiePt.prototype.write.call(this, oups, list);
 oups.writeByte(this.natImage);
 oups.writeInt(this.width);
 oups.writeInt(this.height);
 oups.writeBufferedImage(this.image, this.natImage);
};
